import React, { useState } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import {
  Box,
  Grid,
  Paper,
  TextField,
  Button,
  Typography,
  FormLabel,
  MenuItem,
  Stack,
} from '@mui/material';
import Header from "../Header";
import Footer from "../Footer";
import config from "../common/Config";
import Swal from "sweetalert2";
import '../common/commonStyles.css';

const EmailTemplateEditor = () => {
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [templateContent, setTemplateContent] = useState('');
  const [isUpdate, setIsUpdate] = useState(false);
  const [status, setStatus] = useState('1'); // Default to Active
  const [send_email, setSendEmail] = useState('');
  const accessToken = localStorage.getItem("accessToken");
  const userId = localStorage.getItem("userId");
  // Handle editor change
  const handleEditorChange = (content) => {
    setTemplateContent(content);
  };

  // Handle template selection and fetch data
  const handleTemplateChange = async (e) => {
    const selectedName = e.target.value;
    setTemplateName(selectedName);

    if (selectedName) {
      const response = await fetch(`${config.API_URL}/email_templates_get_by_name`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ name: selectedName }),
      });
      if (response.ok) {
        const data = await response.json();
        setTemplateSubject(data.subject || '');
        setTemplateContent(data.content || '');
        setStatus(data.status !== undefined ? String(data.status) : '1');
        setIsUpdate(!!data.subject);
        setSendEmail(!!data.send_email ? data.send_email : ''); // Set send_email if subject exists
      } else {
        setTemplateSubject('');
        setTemplateContent('');
        setStatus('1');
        setSendEmail(''); // Reset send_email if no data
        setIsUpdate(false);
      }
    } else {
      setTemplateSubject('');
      setTemplateContent('');
      setStatus('1');
      setSendEmail(''); // Reset send_email if no data
      setIsUpdate(false);
    }
  };

  // Insert shortcode at cursor
  const insertShortcode = (shortcode) => {
    const editor = window.tinymce.activeEditor;
    if (editor) {
      const selection = editor.selection;
      selection.setContent(shortcode + '\n');
    }
  };

  // Handle form submit (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    const url = isUpdate
      ? `${config.API_URL}/email_templates_update`
      : `${config.API_URL}/email_templates_store`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authorization': `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        name: templateName,
        subject: templateSubject,
        content: templateContent,
        status: status,
        send_email: send_email,
        user_id: userId,
      }),
    });

   if (response.ok) {
  //setMessage(isUpdate ? 'Template updated successfully!' : 'Template saved successfully!');
  Swal.fire({
    title: "Success",
    text: isUpdate ? "Template updated successfully!" : "Template saved successfully!",
    icon: "success",
    timer: 1500,
    showConfirmButton: false,
  });
} else {
  //setMessage('Failed to save template.');
  Swal.fire({
    title: "Error",
    text: "Failed to save template.",
    icon: "error",
    timer: 1500,
    showConfirmButton: false,
  });
}
  };

  return (
      <Box className="page-container">
        <Paper className="paper-style">
          {/* Title Section */}
          <Box sx={{ px: 2, py: 1.2}}>
            <Typography className="page-title">
              Email Template Editor
            </Typography>
          </Box>

          {/* Form Section */}
          <Box sx={{ px: 2, py: 1.2}}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormLabel component="legend" sx={{ mb:0.5, fontWeight: 600, color: "#212b36", display: "block" }}>
                    Template Name <span style={{ color: "red" }}>*</span>
                  </FormLabel>
                  <TextField
                    className="form-input-border"
                    select
                    fullWidth
                    size="small"
                    value={templateName}
                    onChange={handleTemplateChange}
                    required
                   
                  >
                    <MenuItem value="">Select a Template</MenuItem>
                    <MenuItem value="Place Order">Order Confirmation</MenuItem>
                    <MenuItem value="Order Accepted">Order Accepted</MenuItem>
                    <MenuItem value="Send Tax Invoice">Send Tax Invoice</MenuItem>
                    <MenuItem value="Amount Due">Amount Due</MenuItem>
                    <MenuItem value="Reminder 1 - over due amount">Reminder 1 - Overdue Amount</MenuItem>
                    <MenuItem value="Reminder 2 - overdue amount">Reminder 2 - Overdue Amount</MenuItem>
                    <MenuItem value="Reminder 3 - overdue amount">Reminder 3 - Overdue Amount</MenuItem>
                    <MenuItem value="New account request">New Account Request</MenuItem>
                    <MenuItem value="Account opened">Account Opened</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <FormLabel component="legend" sx={{ mb: 0.5, fontWeight: 600, color: "#212b36", display: "block" }}>
                    Template Subject
                  </FormLabel>
                  <TextField
                    className="form-input-border"
                    fullWidth
                    size="small"
                    value={templateSubject}
                    onChange={(e) => setTemplateSubject(e.target.value)}
                    name="subject"
                    id="subject"
                
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormLabel component="legend" sx={{ mb:0.5, fontWeight: 600, color: "#212b36", display: "block" }}>
                    Template Body
                  </FormLabel>
                  <Box sx={{ border: "1px solid rgba(145, 158, 171, 0.16)", borderRadius: 1, overflow: "hidden" }}>
                    <Editor 
                      value={templateContent}
                      init={{
                        height: 300,
                        menubar: false,
                        plugins: ['link', 'table', 'code'],
                        branding: false,
                        toolbar: 'undo redo | formatselect | bold italic | alignleft aligncenter alignright | link image',
                        content_style: "body { line-height: 0.5; }",
                      }}
                      onEditorChange={handleEditorChange}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <FormLabel component="legend" sx={{ mb: 1, fontWeight: 600, color: "#212b36", display: "block" }}>
                    Shortcodes
                  </FormLabel>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => insertShortcode('{Customer_name}')}
                      sx={{
                        textTransform: "none",
                        borderColor: "rgba(26, 27, 27, 0.79)",
                        color: "#000",
                        "&:hover": {
                          borderColor: "rgba(145, 158, 171, 0.48)",
                          backgroundColor: "rgba(145, 158, 171, 0.08)"
                        }
                      }}
                    >
                      Customer Name
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => insertShortcode('{Company_name}')}
                      sx={{
                        textTransform: "none",
                        borderColor: "rgba(26, 27, 27, 0.79)",
                        color: "#000",
                        "&:hover": {
                          borderColor: "rgba(145, 158, 171, 0.48)",
                          backgroundColor: "rgba(145, 158, 171, 0.08)"
                        }
                      }}
                    >
                      Company Name
                    </Button>
                    {(templateName !== "New account request" && templateName !== "Account opened" && templateName !== "Amount Due" && templateName !== "Reminder 1 - over due amount" && templateName !== "Reminder 2 - overdue amount" && templateName !== "Reminder 3 - overdue amount") && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Order_no}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Order No
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Order_date}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Order Date
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Invoice_date}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Invoice Date
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Order_amount}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Order Amount
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Attachment}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Attachment
                        </Button>
                      </>
                    )}
                    {(templateName === "New account request" || templateName === "Account opened") && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Site_link}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Web Site Link
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{User_name}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          User Name
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Password}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Password
                        </Button>
                      </>
                    )}
                    {(templateName === "Amount Due" || templateName === "Reminder 1 - over due amount" || templateName === "Reminder 2 - overdue amount" || templateName === "Reminder 3 - overdue amount") && (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Amount Due}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Amount Due
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => insertShortcode('{Due_day}')}
                          sx={{
                            textTransform: "none",
                            borderColor: "rgba(26, 27, 27, 0.79)",
                            color: "#000",
                            "&:hover": {
                              borderColor: "rgba(145, 158, 171, 0.48)",
                              backgroundColor: "rgba(145, 158, 171, 0.08)"
                            }
                          }}
                        >
                          Due Day
                        </Button>
                      </>
                    )}
                  </Stack>
                </Grid>

                <Grid item xs={12} md={4}>
                  <FormLabel component="legend" sx={{ mb:0.5, fontWeight: 600, color: "#212b36", display: "block" }}>
                    Status
                  </FormLabel>
                  <TextField
                    className="form-input-border"
                    select
                    fullWidth
                    size="small"
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    name="status"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f5f7fa",
                        "&:hover": {
                          backgroundColor: "#fff"
                        },
                        "&.Mui-focused": {
                          backgroundColor: "#fff"
                        }
                      }
                    }}
                  >
                    <MenuItem value="1">Active</MenuItem>
                    <MenuItem value="0">Inactive</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12} md={8}>
                  <FormLabel component="legend" sx={{ mb:0.5, fontWeight: 600, color: "#212b36", display: "block" }}>
                    Send Email For
                  </FormLabel>
                  <TextField
                    className="form-input-border"
                    select
                    fullWidth
                    size="small"
                    value={send_email}
                    onChange={(e) => setSendEmail(e.target.value)}
                    name="send_email"
              
                  >
                    <MenuItem value="customer">Customer Only</MenuItem>
                    <MenuItem value="company">Company Only</MenuItem>
                    <MenuItem value="both">Both</MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    className="button-variant-contained"
                  >
                    {isUpdate ? 'Update Template' : 'Save Template'}
                  </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Box>
        </Paper>
      </Box>
  );
};

export default EmailTemplateEditor;